import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { LiveDataModel, PowermeterHardwareDataModel } from 'src/State/Storage/StorageModel'
import { useParams } from 'react-router-dom'
import { addLiveData, setPowermeterLiveData } from 'src/State/Storage/StorageActions'
import { useSocketConnectionContext } from 'src/Elements/Framework/SystemDataProvider'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import LiveView from '../LiveView/LiveView'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import LiveDataTitle from './LiveDataTitle'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { makeStyles } from 'tss-react/mui'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { DeviceLinkModel } from 'src/Libs/installations/models'
import { useTypedSelector } from 'src/State/RootReducer'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OfflineOverlay } from '../Overlays'
import { IconButton } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { G1Nowifi, J1TransferarrowB } from '@e3dc-react/icons'
import GuiLiveView from '../LiveView/AlternativeView/GuiLiveView'
import OfflineLiveView from '../LiveView/OfflineLiveView'
import OfflineGuiLiveView from '../LiveView/AlternativeView/OfflineGuiLiveView'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { useUserSettings } from 'src/Hooks'
import { isUndefined } from 'lodash'
import { AnimatePresence, motion } from 'framer-motion'
import { useFarmContext } from '../Farm'
import { AI360AccordionForLiveView, SgReadyBadgeForLiveView } from 'src/Libs/live-view'
import { DASHBOARD_LIVE_VIEW_SWITCH_BUTTON_ID } from 'src/Libs/qa/ids'

const useStyles = makeStyles()(theme => ({
  topRowContent: {
    display: 'flex',
    width: '100%',
    height: '100%',
    paddingInline: theme.spacing(2),
  },
  liveViewWrapper: {
    width: '100%',
    height: '100%',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  wrapperClassName: {
    height: '100%',
  },
  paper: {
    paddingBottom: 0,
    flexGrow: 6,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      height: 'auto',
    },
    height: '100%',
  },
  paperContent: {
    display: 'block',
    paddingTop: theme.spacing(2),
    flexDirection: 'column',
  },
}))

const GUI_LIVE_VIEW_LOADING_MIN_HEIGHT = {
  xs: 660,
  sm: 660,
  md: 632,
}

const REGULAR_LIVE_VIEW_LOADING_MIN_HEIGHT = {
  xs: 420,
  sm: 390,
  md: 360,
}

interface Props {
  wallboxes: WallboxHardwareModel[]
  powermeterHardware: PowermeterHardwareDataModel
  innerRef: React.MutableRefObject<HTMLDivElement | null>
  deviceLinks?: DeviceLinkModel[]
  isFarm: boolean
  hide: boolean
}

/**
 * Wraps the live view which visualizes the energy flow of a system
 */
const LiveViewWrapper: React.FC<Props> = ({ innerRef, isFarm, wallboxes, powermeterHardware, hide }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  /** TODO: get rid of using redux for power-meter live values */
  const liveDataRedux = useTypedSelector(state => state.storage.liveData)
  const { storageId } = useParams<{ installationId: string; storageId: string }>()
  const {
    storageSocketState,
    wallboxChargingSocketState,
    wallboxSocketState,
    batterySocketState,
    emergencyPowerBatterySocketState,
    emergencyPowerStorageSocketState,
  } = useContext(useSocketConnectionContext())
  const { isStorageOnline, areAllClientsOnline } = useContext(InstallationOnlineStatusContext)
  const [showGuiLiveValues, setShowGuiLiveValues] = useState<boolean>(true)
  const downMobile = useMediaQuery(theme.breakpoints.down(Breakpoints.Mobile))
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const { data: emergencyPowerBatterySocketData } = emergencyPowerBatterySocketState
  const isChargeLockActive = emergencyPowerBatterySocketData?.isChargeLockActive || false
  const isDischargeLockActive = emergencyPowerBatterySocketData?.isDischargeLockActive || false
  const isBlockingTimeActive = isChargeLockActive || isDischargeLockActive
  const matchingChargingState = wallboxChargingSocketState.data?.reverse().find(event => event.chargingStatus === 'CHARGING' && event.slotPrice !== 0)
  const limit = Number(storageSocketState.data?.dynamicPowerPrice.priceLimitWallbox ?? 0)
  const isBelowLimit = matchingChargingState?.slotPrice ? matchingChargingState?.slotPrice <= limit : false
  const showDynamicPrices = !!storageSocketState.data?.dynamicPowerPrice && isDynamicPowerPriceEnabled && isBelowLimit
  const { updateUserSettings, userSettings, isUserSettingsLoading } = useUserSettings()

  const OfflineIcon = <G1Nowifi sx={{ color: 'grey.grey03' }} />

  useEffect(() => {
    if (isUndefined(userSettings.guiLiveView)) updateUserSettings({ guiLiveView: true })
    if (userSettings.guiLiveView === false) setShowGuiLiveValues(false)
    else setShowGuiLiveValues(true)
  }, [userSettings.guiLiveView, updateUserSettings, isUserSettingsLoading])

  const liveData: LiveDataModel | undefined = useMemo(() => {
    if (!storageId) return
    const data = storageSocketState.data
    const wbData = wallboxSocketState.data
    const batteryData = batterySocketState.data
    const emergencyBatteryMode = emergencyPowerBatterySocketState.data
    const emergencyStorageMode = emergencyPowerStorageSocketState.data

    let enhancedData: Partial<LiveDataModel> = {
      powermeters: undefined,
      homeNetPower: 0,
      powerBat: 0,
      pvHomePower: 0,
      homeConsumption: 0,
      socCurrent: 0,
      homeWallboxPower: 0,
      emergencyBatteryMode,
      emergencyStorageMode,
    }

    if (data) {
      enhancedData = {
        ...data,
        emergencyBatteryMode,
        emergencyStorageMode,
        powermeters: undefined,
        livePowermeters: data.powermeters,
        homeNetPower: (data.powerRootLmL1 ?? 0) + (data.powerRootLmL2 ?? 0) + (data.powerRootLmL3 ?? 0),
        powerBat: (data.powerBat1 ?? 0) + (data.powerBat2 ?? 0) + (data.powerBat3 ?? 0),
        pvHomePower: (data.powerPvS1 ?? 0) + (data.powerPvS2 ?? 0) + (data.powerPvS3 ?? 0),
        homeConsumption: (data.powerCL1 ?? 0) + (data.powerCL2 ?? 0) + (data.powerCL3 ?? 0),
        socCurrent: batteryData?.soc ?? data.soc,
      }

      if (wbData) {
        enhancedData = {
          ...enhancedData,
          homeWallboxPower: wbData.reduce((prev, curr) => {
            if (curr.l1 !== undefined && curr.l2 !== undefined && curr.l3 !== undefined) {
              return prev + curr.l1 + curr.l2 + curr.l3
            }
            return prev
          }, 0),
        }
      }
    }

    return enhancedData as LiveDataModel
  }, [storageId, storageSocketState, wallboxSocketState, batterySocketState, emergencyPowerBatterySocketState, emergencyPowerStorageSocketState])

  useEffect(() => {
    if (liveData) {
      dispatch(addLiveData(liveData))
      dispatch(setPowermeterLiveData(storageSocketState.data?.powermeters ?? []))
    }
  }, [liveData, storageSocketState.data?.powermeters, dispatch])

  if (hide) return null

  const changeLiveView = (): void => {
    updateUserSettings({ guiLiveView: !showGuiLiveValues })
    localStorage.setItem('guiLiveView', String(!showGuiLiveValues)) // still set this in localStorage for later use
    setShowGuiLiveValues(!showGuiLiveValues)
  }

  const renderLiveView = (mode: 'gui' | 'regular'): React.ReactElement => {
    const isGui = mode === 'gui'
    const loadingMinHeight = isGui ? GUI_LIVE_VIEW_LOADING_MIN_HEIGHT : REGULAR_LIVE_VIEW_LOADING_MIN_HEIGHT

    if (!liveData || isUserSettingsLoading) {
      return (
        <Box
          sx={{
            minHeight: loadingMinHeight,
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading />
        </Box>
      )
    }

    const liveViewProps = {
      batteryHomePower: Math.sign(liveData.powerBat) === -1 ? Math.abs(liveData.powerBat) : 0,
      homeBatteryPower: Math.sign(liveData.powerBat) === 1 ? Math.abs(liveData.powerBat) : 0,
      homeWallboxPower: liveData.homeWallboxPower ?? 0,
      wallboxes: wallboxes,
      consumers: powermeterHardware?.consumers,
      producers: [...(powermeterHardware?.producers ?? []), ...(powermeterHardware?.bypassNoCharge ?? [])],
      homeNetworkPower: Math.sign(liveData.homeNetPower) === -1 ? Math.abs(liveData.homeNetPower) : 0,
      networkHomePower: Math.sign(liveData.homeNetPower) === 1 ? Math.abs(liveData.homeNetPower) : 0,
      pvHomePower: liveData.pvHomePower,
      homeConsumerPower: liveDataRedux?.homeConsumerPower ?? 0,
      consumption: liveData.homeConsumption ?? 0,
      trackerHomePower: Math.abs(liveDataRedux?.trackerHomePower ?? 0),
      isFarm,
      liveData: liveDataRedux,
      offlineIcon: OfflineIcon,
      shallowLiveView: downMobile,
      isDynamicPowerPriceEnabled,
      isBlockingTimeActive,
      showDynamicPrices,
      socCurrent: liveData.socCurrent ?? 0,
    }

    if (isStorageOnline) {
      return isGui ? <GuiLiveView storageId={storageId} {...liveViewProps} /> : <LiveView storageId={storageId} {...liveViewProps} />
    }

    return isGui ? (
      <OfflineGuiLiveView
        storageId={storageId}
        wallboxes={wallboxes}
        consumers={powermeterHardware?.consumers}
        producers={[...(powermeterHardware?.producers ?? []), ...(powermeterHardware?.bypassNoCharge ?? [])]}
        isFarm={isFarm}
        offlineIcon={OfflineIcon}
        shallowLiveView={downMobile}
      />
    ) : (
      <OfflineLiveView
        wallboxes={wallboxes}
        consumers={powermeterHardware?.consumers}
        producers={[...(powermeterHardware?.producers ?? []), ...(powermeterHardware?.bypassNoCharge ?? [])]}
        isFarm={isFarm}
        offlineIcon={OfflineIcon}
        shallowLiveView={downMobile}
        storageId={storageId}
      />
    )
  }

  return (
    <OfflineOverlay isOffline={!isStorageOnline} sxOverlay={{ height: '100%' }}>
      <LiveViewPaper
        areAllClientsOnline={areAllClientsOnline ?? false}
        isStorageOnline={isStorageOnline}
        showGuiLiveValues={showGuiLiveValues}
        changeLiveView={changeLiveView}
        innerRef={innerRef}
      >
        <AnimatePresence exitBeforeEnter>
          {showGuiLiveValues && (
            <motion.div
              key={'gui-live-view'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              style={{ height: '100%' }}
            >
              {renderLiveView('gui')}
            </motion.div>
          )}
          {!showGuiLiveValues && (
            <motion.div
              key={'regular-live-view'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              style={{ height: '100%' }}
            >
              {renderLiveView('regular')}
            </motion.div>
          )}
        </AnimatePresence>
      </LiveViewPaper>
    </OfflineOverlay>
  )
}

export type LiveViewPaperProps = {
  children: React.ReactNode
  areAllClientsOnline: boolean
  isStorageOnline: boolean
  showGuiLiveValues: boolean
  innerRef?: React.MutableRefObject<HTMLDivElement | null>
  changeLiveView: () => void
}

export const LiveViewPaper: React.FC<LiveViewPaperProps> = ({
  children,
  areAllClientsOnline,
  isStorageOnline,
  showGuiLiveValues,
  innerRef,
  changeLiveView,
}) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { isFarmMasterSelected } = useFarmContext()
  const { classes } = useStyles()

  return (
    <Paper
      title={<LiveDataTitle showOfflineIndicator={!areAllClientsOnline && isFarmMasterSelected && isStorageOnline} />}
      className={classes.paper}
      contentClassName={classes.paperContent}
      wrapperClassName={classes.wrapperClassName}
      headerChildren={
        <IconButton
          icon={<J1TransferarrowB color={showGuiLiveValues ? undefined : 'primary'} />}
          onClick={changeLiveView}
          dataQa={DASHBOARD_LIVE_VIEW_SWITCH_BUTTON_ID}
        />
      }
      id="dashboard-liveview"
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        }}
      >
        <Box className={classes.topRowContent}>
          <Box
            className={classes.liveViewWrapper}
            sx={{ paddingRight: showGuiLiveValues ? 2 : 0, paddingLeft: showGuiLiveValues ? 2 : 0 }}
            ref={innerRef}
          >
            {children}
          </Box>
        </Box>

        <SgReadyBadgeForLiveView isNewLiveValuesEnabled={showGuiLiveValues} installationId={installationId} storageId={storageId} />
        <AI360AccordionForLiveView />
      </Box>
    </Paper>
  )
}

export default LiveViewWrapper
